import { useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";

interface Permission {
    name: string;
    description: string;
    admin: boolean;
    adminManager: boolean;
    user: boolean;
    external: boolean;
}

type Region = "US" | "EMEA";

export default function RolePermissionsPage() {
    const [selectedRegion, setSelectedRegion] = useState<Region>("US");
    const permissions: Permission[] = [
        {
            name: "Add New User",
            description: "Create new user accounts in the system",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Edit User Identity",
            description: "Modify user name, email, login ID, and access level/role",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Edit Other User Assignments",
            description: "Modify other users' project, discipline, and sequence/CACC assignments",
            admin: true,
            adminManager: true,
            user: false,
            external: false,
        },
        {
            name: "Delete Users",
            description: "Remove user accounts from the system",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "View All Users",
            description: "Access the All Users page and view user list",
            admin: true,
            adminManager: true,
            user: true,
            external: true,
        },
        {
            name: "Edit Own Project Assignment",
            description: "Change their own project assignment",
            admin: true,
            adminManager: true,
            user: true,
            external: true,
        },
        {
            name: "Edit Own Discipline Assignment",
            description: "Change their own discipline assignment",
            admin: true,
            adminManager: true,
            user: true,
            external: true,
        },
        {
            name: "Edit Own Sequence Assignment",
            description: "Change their own sequence/CACC assignment",
            admin: true,
            adminManager: true,
            user: true,
            external: true,
        },
        {
            name: "Access Edit Loaded Maps",
            description: "View and manage loaded maps for projects",
            admin: true,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Access Models",
            description: "View and manage inspection models",
            admin: true,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Access Checklist Manager",
            description: "Manage checklists and inspection templates",
            admin: true,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Edit Projects",
            description: "Create, modify, and archive projects",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "View Projects Page",
            description: "Access the Projects management page",
            admin: true,
            adminManager: true,
            user: true,
            external: true,
        },
        {
            name: "Add/Edit Project Maps",
            description: "Add new maps or edit existing project maps",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Delete Punch List Issues",
            description: "Permanently delete punch list items",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Cancel Own Punch Lists",
            description: "Mark their own created punch list issues as cancelled",
            admin: true,
            adminManager: true,
            user: true,
            external: true,
        },
    ];

    const emeaPermissions: Permission[] = [
        {
            name: "Add New User",
            description: "Create new user accounts in the system",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Edit User Identity",
            description: "Modify user name, email, login ID, and access level/role",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Edit Other User Assignments",
            description: "Modify other users' project, discipline, and sequence/CACC assignments",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Delete Users",
            description: "Remove user accounts from the system",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "View All Users",
            description: "Access the All Users page and view user list",
            admin: false,
            adminManager: false,
            user: true,
            external: true,
        },
        {
            name: "Edit Own Project Assignment",
            description: "Change their own project assignment",
            admin: false,
            adminManager: false,
            user: true,
            external: true,
        },
        {
            name: "Edit Own Discipline Assignment",
            description: "Change their own discipline assignment",
            admin: false,
            adminManager: false,
            user: true,
            external: true,
        },
        {
            name: "Edit Own Sequence Assignment",
            description: "Change their own sequence/CACC assignment",
            admin: false,
            adminManager: false,
            user: true,
            external: true,
        },
        {
            name: "Access Edit Loaded Maps",
            description: "View and manage loaded maps for projects",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Access Models",
            description: "View and manage inspection models",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Access Checklist Manager",
            description: "Manage checklists and inspection templates",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Edit Projects",
            description: "Create, modify, and archive projects",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "View Projects Page",
            description: "Access the Projects management page",
            admin: false,
            adminManager: false,
            user: true,
            external: true,
        },
        {
            name: "Add/Edit Project Maps",
            description: "Add new maps or edit existing project maps",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Delete Punch List Issues",
            description: "Permanently delete punch list items",
            admin: false,
            adminManager: false,
            user: false,
            external: false,
        },
        {
            name: "Cancel Own Punch Lists",
            description: "Mark their own created punch list issues as cancelled",
            admin: false,
            adminManager: false,
            user: true,
            external: true,
        },
    ];

    // Define roles for each region
    const usRoles = [
        { key: "admin", label: "Admin", color: "text-blue-600" },
        { key: "adminManager", label: "Admin Manager", color: "text-purple-600" },
        { key: "user", label: "User", color: "text-green-600" },
        { key: "external", label: "External", color: "text-orange-600" },
    ];

    const emeaRoles = [
        { key: "user", label: "User (EMEA)", color: "text-green-600" },
        { key: "external", label: "External (EMEA)", color: "text-orange-600" },
    ];

    // Get current roles based on selected region
    const roles = selectedRegion === "US" ? usRoles : emeaRoles;
    const currentPermissions = selectedRegion === "US" ? permissions : emeaPermissions;

    const PermissionIcon = ({ allowed }: { allowed: boolean }) => {
        return allowed ? (
            <FaCheck className="text-green-600 mx-auto" size={18} />
        ) : (
            <FaTimes className="text-red-400 mx-auto" size={18} />
        );
    };

    return (
        <div className="overflow-x-auto p-4">
            {/* Region Selector */}
            <div className="mb-6">
                <div className="flex gap-2 border-b border-gray-300 dark:border-gray-600">
                    <button
                        onClick={() => setSelectedRegion("US")}
                        className={`px-6 py-3 font-semibold transition-colors relative ${
                            selectedRegion === "US"
                                ? "text-orange-600 dark:text-orange-400"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                    >
                        US Roles
                        {selectedRegion === "US" && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600 dark:bg-orange-400" />
                        )}
                    </button>
                    <button
                        onClick={() => setSelectedRegion("EMEA")}
                        className={`px-6 py-3 font-semibold transition-colors relative ${
                            selectedRegion === "EMEA"
                                ? "text-orange-600 dark:text-orange-400"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                    >
                        EMEA Roles
                        {selectedRegion === "EMEA" && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600 dark:bg-orange-400" />
                        )}
                    </button>
                </div>
            </div>

            <div className="mb-6">
                <h1 className="text-3xl font-bold mb-2">Role Permissions - {selectedRegion}</h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Overview of permissions for each role in the UNITE system.
                    <span className="block mt-1 text-sm italic">
                        Note: SuperAdmin has full access to all features and is not shown in this table.
                    </span>
                </p>
            </div>

            <div className="overflow-x-auto shadow-md rounded-lg">
                <table className="min-w-full text-sm border-collapse">
                    <thead className="bg-gray-100 dark:bg-gray-800 border-b-2 border-gray-300 dark:border-gray-600">
                        <tr>
                            <th className="px-4 py-3 text-left font-semibold sticky left-0 bg-gray-100 dark:bg-gray-800 z-[1]">
                                Permission
                            </th>
                            {roles.map((role) => (
                                <th
                                    key={role.key}
                                    className={`px-4 py-3 text-center font-semibold ${role.color}`}
                                >
                                    {role.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentPermissions.map((permission, index) => (
                            <tr
                                key={permission.name}
                                className={`border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                    index % 2 === 0
                                        ? "bg-white dark:bg-gray-900"
                                        : "bg-gray-50 dark:bg-gray-800"
                                }`}
                            >
                                <td className="px-4 py-3 sticky left-0 bg-inherit z-[1]">
                                    <div className="font-medium">{permission.name}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {permission.description}
                                    </div>
                                </td>
                                {roles.map((role) => (
                                    <td key={role.key} className="px-4 py-3 text-center">
                                        <PermissionIcon allowed={permission[role.key as keyof Permission] as boolean} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 rounded-lg">
                <h3 className="font-semibold mb-2 text-blue-900 dark:text-blue-300">Legend:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center">
                        <FaCheck className="text-green-600 mr-2" />
                        <span>Permission granted</span>
                    </div>
                    <div className="flex items-center">
                        <FaTimes className="text-red-400 mr-2" />
                        <span>Permission denied</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
